import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, MapPinned, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  platformSitesApi, PlatformSiteRow, platformTenantsApi, PlatformTenant,
} from "@/lib/platform-api";
import { ApiError } from "@/lib/api";

const PAGE_SIZE = 25;

export default function SitesOverview() {
  const [rows, setRows] = useState<PlatformSiteRow[]>([]);
  const [summary, setSummary] = useState({ total: 0, active: 0, inactive: 0 });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [tenantFilter, setTenantFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tenants, setTenants] = useState<PlatformTenant[]>([]);

  useEffect(() => {
    platformTenantsApi.list({ pageSize: 200 }).then((r) => setTenants(r.data)).catch(() => { /* non-fatal */ });
  }, []);

  useEffect(() => {
    const id = setTimeout(() => { setDebounced(search); setPage(1); }, 300);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    platformSitesApi
      .overview({
        page,
        pageSize: PAGE_SIZE,
        search: debounced || undefined,
        tenantId: tenantFilter === "all" ? undefined : tenantFilter,
        status: statusFilter === "all" ? undefined : (statusFilter as "ACTIVE" | "INACTIVE"),
      })
      .then((r) => { if (!cancelled) { setRows(r.data); setSummary(r.summary); setTotal(r.total); } })
      .catch((e) => { if (!cancelled) toast.error(e instanceof ApiError ? e.message : "Failed to load sites"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, debounced, tenantFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const todaysRecords = rows.reduce((s, r) => s + r.attendanceToday, 0);
  const openAlerts = rows.reduce((s, r) => s + r.alertsToday, 0);

  const tiles = [
    { label: "Total sites", value: summary.total },
    { label: "Active sites", value: summary.active },
    { label: "Today's records (page)", value: todaysRecords },
    { label: "Open alerts (page)", value: openAlerts },
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {tiles.map((t) => (
          <Card key={t.label} className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t.label}</div>
            <div className="mt-1 font-display text-xl font-bold tabular-nums">{t.value.toLocaleString()}</div>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="border-b pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2"><MapPinned className="h-5 w-5 text-primary" /><CardTitle className="text-base">All sites ({total.toLocaleString()})</CardTitle></div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search site name / code..." className="h-9 w-56 pl-8" />
              </div>
              <Select value={tenantFilter} onValueChange={(v) => { setTenantFilter(v); setPage(1); }}>
                <SelectTrigger className="h-9 w-44"><SelectValue placeholder="Tenant" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All tenants</SelectItem>
                  {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="h-9 w-32"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="INACTIVE">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Site</TableHead><TableHead>Tenant</TableHead><TableHead>Region</TableHead>
                <TableHead className="text-right">Employees</TableHead><TableHead className="text-right">Geofence</TableHead>
                <TableHead className="text-right">Today</TableHead><TableHead>Alerts</TableHead><TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="h-24 text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin text-muted-foreground" /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="h-24 text-center text-sm text-muted-foreground">No sites match your filters.</TableCell></TableRow>
              ) : rows.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="text-sm font-medium">{s.name}<span className="ml-1.5 text-xs text-muted-foreground">{s.code}</span></TableCell>
                  <TableCell className="text-sm">{s.tenantName ?? "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{s.region ?? "—"}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{s.employees}</TableCell>
                  <TableCell className="text-right text-xs text-muted-foreground tabular-nums">{s.geofenceRadiusM == null ? "inherited" : `${s.geofenceRadiusM}m`}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{s.attendanceToday}</TableCell>
                  <TableCell>
                    {s.alertsToday > 0
                      ? <Badge variant="outline" className="border-warning/30 bg-warning/10 text-warning">{s.alertsToday}</Badge>
                      : <span className="text-xs text-muted-foreground">0</span>}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={s.status === "ACTIVE" ? "border-success/30 bg-success/10 text-success" : "border-muted-foreground/30 bg-muted text-muted-foreground"}>
                      {s.status === "ACTIVE" ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t px-4 py-2.5 text-xs text-muted-foreground">
            <span>Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
